/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import { isBefore } from 'date-fns/isBefore';
import { waitFor } from '@ember/test-waiters';
import { didCancel, race, restartableTask, timeout } from 'ember-concurrency';
import { dasherize } from '@ember/string';
import { type Select } from 'ember-power-select/components/power-select';
import { type IDropdown } from 'ember-bootstrap/components/bs-dropdown';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { toggleValue } from 'uplisting-frontend/utils/toggle-value';
import { searchOptions } from 'uplisting-frontend/utils/search';
import { pluralize } from 'ember-inflector';
import PlaceholderComponent from 'uplisting-frontend/components/ui/occasion-search-bar-placeholder';
import PropertyModel from 'uplisting-frontend/models/property';
import PropertyTagModel from 'uplisting-frontend/models/property-tag';
import { isAnyFocused } from 'uplisting-frontend/utils/active-focus';

interface IArgs {
  onLoadData?(): Promise<void>;
  hidePropertiesFilter?: boolean;
}

interface SearchHeaderSignature {
  Element: HTMLDivElement;

  Args: IArgs;
}

export default class UiSearchHeaderV1Component extends Component<SearchHeaderSignature> {
  @service router!: Services['router'];
  @service storage!: Services['storage'];

  @service('repositories/stay')
  stayRepository!: Services['repositories/stay'];

  @service('repositories/property')
  propertyRepository!: Services['repositories/property'];

  @service('repositories/property-tag')
  propertyTagRepository!: Services['repositories/property-tag'];

  @tracked inputValue = '';
  @tracked selectedItems!: (
    | ModelRegistry['property']
    | ModelRegistry['property-tag']
  )[];

  @tracked stays: ModelRegistry['stay'][] = [];

  powerSelectAPI!: Select;
  searchTimeout?: number;

  constructor(owner: Owner, args: IArgs) {
    super(owner, args);

    const { propertiesConfigData } = this.storage;

    Object.assign(this, {
      selectedItems: [
        ...propertiesConfigData.properties,
        ...propertiesConfigData.propertyTags,
      ],
    });
  }

  get placeholderComponent(): typeof PlaceholderComponent {
    return PlaceholderComponent;
  }

  @cached
  get properties(): ModelRegistry['property'][] {
    return this.propertyRepository.peekAll();
  }

  @cached
  get filteredProperties(): ModelRegistry['property'][] {
    return searchOptions<ModelRegistry['property']>(
      this.properties,
      this.inputValue,
      'searchName',
    );
  }

  @cached
  get propertyTags(): ModelRegistry['property-tag'][] {
    return this.propertyTagRepository
      .peekAll()
      .filter((tag) => tag.properties.length);
  }

  @cached
  get filteredPropertyTags(): ModelRegistry['property-tag'][] {
    return searchOptions<ModelRegistry['property-tag']>(
      this.propertyTags,
      this.inputValue,
      'searchName',
    );
  }

  @cached
  get noDataMatches(): boolean {
    return !this.filteredProperties.length && !this.filteredPropertyTags.length;
  }

  @cached
  get selectedStayRoute(): string {
    const currentRouteName = this.router.currentRouteName ?? '';

    const isActionRoute = currentRouteName.includes('action');
    const isXCalendarRoute = currentRouteName.includes('xcalendar');

    if (isActionRoute) {
      return 'action';
    } else if (isXCalendarRoute) {
      return 'xcalendar';
    }

    return 'calendar';
  }

  get ongoingStays(): ModelRegistry['stay'][] {
    return this.stays.filter((item) => item.isOngoing && !item.isCancelled);
  }

  get pastStays(): ModelRegistry['stay'][] {
    return this.stays
      .filter((item) => item.isInPast && !item.isCancelled)
      .reverse();
  }

  get futureStays(): ModelRegistry['stay'][] {
    return this.stays.filter((item) => item.isInFuture && !item.isCancelled);
  }

  get cancelledStays(): ModelRegistry['stay'][] {
    return this.stays.filter((item) => item.isCancelled);
  }

  get staysGrouped(): ModelRegistry['stay'][] {
    const ongoing = this.ongoingStays;
    const past = this.pastStays;
    const future = this.futureStays;
    const cancelled = this.cancelledStays;

    if (past.length) {
      (past[0] as ModelRegistry['stay']).pastDelimiter = true;
    }

    if (cancelled.length) {
      (cancelled[0] as ModelRegistry['stay']).cancelledDelimiter = true;
    }

    return [...ongoing, ...future, ...past, ...cancelled];
  }

  @action
  onInput(text: string): boolean {
    return text.length > 2;
  }

  @action
  handleInputSearch(value: string): void {
    this.inputValue = value;
  }

  @action
  handleClosePropertiesSelectorFilter(): void {
    this.inputValue = '';
  }

  @action
  registerAPI(api: Select): void {
    this.powerSelectAPI = api;
  }

  @action
  handleOpenSearch(): void {
    if (isAnyFocused()) {
      return;
    }

    this.powerSelectAPI.actions.open();
  }

  @action
  handleCloseStaysSelector(): void {
    this.stayRepository.unloadAll();
  }

  @action
  @waitFor
  async handleSelectStay(stay: ModelRegistry['stay']): Promise<void> {
    const id = stay.id.split('-')[1] as string;

    await this.router.transitionTo(
      `${this.selectedStayRoute}.${pluralize(dasherize(stay.type))}.summary`,
      id,
    );
  }

  @action
  @waitFor
  async handleItemClick(
    item: ModelRegistry['property'] | ModelRegistry['property-tag'],
  ): Promise<void> {
    toggleValue(this, 'selectedItems', item);

    this.#writeSelectedItems();

    try {
      await this.args.onLoadData?.();
    } catch (e) {
      if (!didCancel(e)) {
        throw e;
      }
    }
  }

  @action
  @waitFor
  async handleUnselectAllProperties(dd: IDropdown): Promise<void> {
    this.selectedItems = [];

    this.#writeSelectedItems();

    dd.closeDropdown();

    try {
      await this.args.onLoadData?.();
    } catch (e) {
      if (!didCancel(e)) {
        throw e;
      }
    }
  }

  fetchStaysTask = restartableTask(async (query: string) => {
    this.stays = [];

    await timeout(600);

    const request = this.stayRepository.query({ query });

    const stays = this.searchTimeout
      ? await race([request, timeout(this.searchTimeout)])
      : await request;

    if (!stays) {
      throw new Error('The guest search timed out');
    }

    const sorted = stays.slice().sort((x, b) => {
      // Dateless (Vrbo) enquiries have no checkOut — isBefore(null, date) is
      // true in date-fns, which would cluster them at the front and make the
      // comparator non-transitive. Sort them to the end instead.
      if (!x.checkOut || !b.checkOut) {
        if (x.checkOut) {
          return -1;
        }

        if (b.checkOut) {
          return 1;
        }

        return 0;
      }

      if (isBefore(x.checkOutDate, b.checkOutDate)) {
        return -1;
      } else if (isBefore(b.checkOutDate, x.checkOutDate)) {
        return 1;
      }

      return 0;
    });

    this.stays = sorted;

    return this.staysGrouped;
  });

  #writeSelectedItems(): void {
    const { selectedItems } = this;

    this.storage.propertiesConfig = {
      propertyIds: selectedItems
        .filter((item) => item instanceof PropertyModel)
        .map((item) => item.id),

      propertyTagIds: selectedItems
        .filter((item) => item instanceof PropertyTagModel)
        .map((item) => item.id),
    };
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Search::Header::V1': typeof UiSearchHeaderV1Component;
  }
}
